/**
 * FeaturedArtistsPage — the "Hoard Artist" gallery.
 * Grid of 3-per-row cards: photo, short intro, next ~3 dates, socials,
 * "Visit Artist Page →". Designed to be the social-media-shareable showcase
 * of bands & artists who support THE WALL.
 */
import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Music, ArrowLeft, ArrowRight, Sparkles, ExternalLink, Calendar as CalIcon, MapPin,
  Instagram, Facebook, Youtube, Globe, Mic2, Trophy, Headphones,
} from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import AlbumPlayer from "../components/AlbumPlayer";
import MusicCrossNav from "../components/MusicCrossNav";
import { API, useAuth } from "../App";
import { toast } from "sonner";
import { ArrowUp, ArrowDown, GripVertical } from "lucide-react";
import { Hoard } from "../components/Hoard";
import { NorthStarSection } from "../components/NorthStarSection";

const fmtDate = (iso) => iso
  ? new Date(iso).toLocaleDateString([], { timeZone: "America/Los_Angeles", weekday: "short", month: "short", day: "numeric" })
  : "";

// Small icon-only pill used inside cards — saves horizontal space.
const SocialDot = ({ href, Icon, label, color }) => {
  if (!href) return null;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      title={label}
      className="w-8 h-8 rounded-full flex items-center justify-center text-white hover:opacity-85 transition-opacity no-underline"
      style={{ backgroundColor: color }}
      data-testid={`artist-card-social-${label.toLowerCase()}`}
    >
      <Icon className="h-4 w-4" />
    </a>
  );
};

export const ArtistCard = ({ artist, upcoming, tracks }) => {
  // Take the artist's next 3 shows from the prebuilt index
  const shows = (upcoming || []).slice(0, 3);
  const profileHref = artist.profile_url || `/artists/${artist.slug}`;
  const artistNameLower = (artist?.name || "").toLowerCase();
  const myTracks = (tracks || []).filter(t =>
    (t.artist_slug && t.artist_slug === artist?.slug) ||
    (t.artist_name && t.artist_name.toLowerCase() === artistNameLower)
  );

  return (
    <div
      className="bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-shadow border border-[#E2E0DC] flex flex-col"
      data-testid={`featured-artist-card-${artist.slug}`}
    >
      {/* Photo */}
      <Link
        to={profileHref}
        className="block aspect-[5/4] overflow-hidden bg-[#1A1A2E] no-underline"
        data-testid={`featured-artist-photo-${artist.slug}`}
      >
        {artist.photo_url ? (
          <img
            src={artist.photo_url}
            alt={artist.name}
            className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-white/30">
            <Music className="h-16 w-16" />
          </div>
        )}
      </Link>

      <div className="p-4 flex flex-col flex-1">
        {/* Hoard badge */}
        <div className="flex items-center gap-1.5 mb-1">
          <Sparkles className="h-3.5 w-3.5 text-[#FFB347]" />
          <span className="text-[10px] uppercase tracking-widest text-[#FFB347] font-bold">Artist of THE WALL</span>
        </div>
        <h3 className="font-['Playfair_Display'] text-lg font-semibold text-[#1A1A1A] leading-tight mb-1">
          {artist.name}
        </h3>
        {artist.city && (
          <p className="text-xs text-[#5C5C5C] mb-2 flex items-center gap-1">
            <MapPin className="h-3 w-3" /> {artist.city}
          </p>
        )}

        {/* Bio intentionally removed from the card so we can show 3 artists
            per row at a comfortable height. The full bio still lives on the
            artist's detail page (/artists/:slug). */}

        {/* Next few shows */}
        {shows.length > 0 && (
          <div className="mb-3 pt-3 border-t border-[#F0EFEB]">
            <p className="text-[10px] uppercase tracking-widest text-[#5C5C5C] font-bold mb-1.5">Next shows</p>
            <ul className="space-y-1 m-0 p-0 list-none">
              {shows.map(s => (
                <li key={s.event_id} className="text-xs">
                  <Link
                    to={`/event/${s.event_id}`}
                    className="flex items-start gap-1.5 hover:bg-[#FAF9F6] rounded p-1 -mx-1 transition-colors no-underline text-[#1A1A1A]"
                    data-testid={`featured-artist-show-${s.event_id}`}
                  >
                    <CalIcon className="h-3 w-3 text-[#9B59B6] mt-0.5 flex-shrink-0" />
                    <span className="flex-1 min-w-0">
                      <span className="font-semibold">{fmtDate(s.start_datetime)}</span>
                      {s.venue_name && (
                        <span className="text-[#5C5C5C]"> · {s.venue_name}</span>
                      )}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Socials */}
        {artist.socials && Object.values(artist.socials).some(Boolean) && (
          <div className="flex gap-1.5 flex-wrap mb-3">
            <SocialDot href={artist.socials.website}    Icon={Globe}     label="Website"   color="#2E5C55" />
            <SocialDot href={artist.socials.instagram}  Icon={Instagram} label="Instagram" color="#E4405F" />
            <SocialDot href={artist.socials.facebook}   Icon={Facebook}  label="Facebook"  color="#1877F2" />
            <SocialDot href={artist.socials.youtube}    Icon={Youtube}   label="YouTube"   color="#FF0000" />
            <SocialDot href={artist.socials.spotify}    Icon={Music}     label="Spotify"   color="#1DB954" />
            <SocialDot href={artist.socials.soundcloud} Icon={Mic2}      label="SoundCloud" color="#FF5500" />
            <SocialDot href={artist.socials.bandcamp}   Icon={Music}     label="Bandcamp"  color="#1DA0C3" />
          </div>
        )}

        {/* Music player — collapsed by default, expands inline. Lives WITH the
            artist instead of in a separate Music Room so visitors discover the
            music in context. */}
        {myTracks.length > 0 && (
          <AlbumPlayer tracks={myTracks} artistName={artist.name} accentColor="#9B59B6" compact />
        )}

        <Link
          to={profileHref}
          className="mt-auto inline-flex items-center gap-1 text-xs font-semibold text-[#2E5C55] hover:text-[#E76F51] transition-colors no-underline"
          data-testid={`featured-artist-visit-${artist.slug}`}
        >
          Visit Artist Page <ExternalLink className="h-3 w-3" />
        </Link>
      </div>
    </div>
  );
};

// Community Spotlight — the big hero card for a prominently-featured artist/band
// (driven by `spotlight: true` on the featured_artists record). Reusable
// template for outreach: set the spotlight fields and this renders automatically.
export const SpotlightCard = ({ artist }) => {
  const profileHref = artist.profile_url || `/artists/${artist.slug}`;
  const s = artist.socials || {};
  return (
    <div
      className="mb-8 rounded-3xl overflow-hidden border border-[#FFB347]/40 bg-gradient-to-br from-[#0A0A0F] via-[#3a2056] to-[#0d6b6b] shadow-2xl"
      data-testid={`featured-artist-spotlight-${artist.slug}`}
    >
      <div className="grid md:grid-cols-[minmax(0,1fr)_1.4fr]">
        {/* Photo */}
        <Link
          to={profileHref}
          className="block relative aspect-[4/5] md:aspect-auto md:min-h-[380px] overflow-hidden bg-[#0A0A0F] no-underline group"
          data-testid={`spotlight-photo-${artist.slug}`}
        >
          {artist.photo_url ? (
            <img
              src={artist.photo_url}
              alt={artist.name}
              className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-white/20">
              <Music className="h-20 w-20" />
            </div>
          )}
        </Link>

        {/* Copy */}
        <div className="p-6 sm:p-8 md:p-10 flex flex-col justify-center text-white">
          <div className="inline-flex items-center gap-2 mb-3">
            <span className="inline-flex items-center gap-1.5 bg-[#FFB347] text-[#1A1A1A] text-[10px] font-black uppercase tracking-widest px-3 py-1 rounded-full">
              <Trophy className="h-3 w-3" /> #1 Community Spotlight
            </span>
          </div>
          {artist.spotlight_eyebrow && (
            <p className="text-[11px] sm:text-xs font-bold uppercase tracking-[0.2em] text-[#FFB347] mb-2">
              {artist.spotlight_eyebrow}
            </p>
          )}
          <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl md:text-5xl font-semibold leading-tight mb-2">
            {artist.name}
          </h2>
          {artist.city && (
            <p className="text-sm text-white/70 mb-3 flex items-center gap-1.5">
              <MapPin className="h-3.5 w-3.5" /> {artist.city}
            </p>
          )}
          {artist.bio && (
            <p className="text-sm sm:text-base text-white/85 leading-relaxed mb-4 max-w-2xl">
              {artist.bio}
            </p>
          )}

          {artist.spotlight_note && (
            <div className="rounded-xl bg-white/10 border border-white/15 px-4 py-3 mb-5 flex items-start gap-2.5">
              {artist.single_cover && (
                <img src={artist.single_cover} alt={artist.single_title || ""} className="w-12 h-12 rounded-lg object-cover flex-shrink-0 border border-white/20" loading="lazy" />
              )}
              <p className="text-xs sm:text-sm text-white/90 leading-relaxed m-0">{artist.spotlight_note}</p>
            </div>
          )}

          {/* Socials */}
          <div className="flex gap-2 flex-wrap mb-5">
            <SocialDot href={s.website}    Icon={Globe}     label="Website"   color="#2E5C55" />
            <SocialDot href={s.spotify}    Icon={Headphones} label="Spotify"  color="#1DB954" />
            <SocialDot href={s.youtube}    Icon={Youtube}   label="YouTube"   color="#FF0000" />
            <SocialDot href={s.instagram}  Icon={Instagram} label="Instagram" color="#E4405F" />
            <SocialDot href={s.facebook}   Icon={Facebook}  label="Facebook"  color="#1877F2" />
          </div>

          <div className="flex flex-wrap gap-3">
            <Link
              to={profileHref}
              className="inline-flex items-center gap-2 bg-[#FFB347] text-[#1A1A1A] rounded-full px-5 py-3 text-sm font-bold uppercase tracking-wide shadow-lg hover:translate-x-0.5 transition-transform no-underline"
              data-testid={`spotlight-visit-${artist.slug}`}
            >
              Visit {artist.name.split(" ")[0]}'s Page <ArrowRight className="h-4 w-4" />
            </Link>
            {artist.single_stream_url && (
              <a
                href={artist.single_stream_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-white/15 border border-white/25 text-white rounded-full px-5 py-3 text-sm font-bold uppercase tracking-wide hover:bg-white/25 transition-colors no-underline"
                data-testid={`spotlight-stream-${artist.slug}`}
              >
                <Headphones className="h-4 w-4" /> Hear &ldquo;{artist.single_title}&rdquo;
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const FeaturedArtistsPage = () => {
  const [artists, setArtists] = useState([]);
  const [eventsByArtist, setEventsByArtist] = useState({});
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [featuredBand, setFeaturedBand] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/touring-bands/featured/current`)
      .then(r => { if (!cancelled) setFeaturedBand(r.data?.band || null); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    // 1) Featured artists roster  2) all music events  3) all music trove items
    Promise.all([
      axios.get(`${API}/featured-artists`).then(r => r.data).catch(() => []),
      Promise.all(["live-music", "open-mic", "karaoke"].map(c =>
        axios.get(`${API}/events?category=${c}`).then(r => r.data).catch(() => [])
      )).then(arrs => arrs.flat()),
      axios.get(`${API}/trove?item_type=music`).then(r => r.data).catch(() => []),
    ]).then(([a, ev, mu]) => {
      if (cancelled) return;
      try {
        const now = Date.now();
        const idx = {};
        (ev || []).forEach(e => {
          if (!e?.host_slug) return;
          if (new Date(e.start_datetime).getTime() < now - 60 * 60 * 1000) return;
          if (!idx[e.host_slug]) idx[e.host_slug] = [];
          idx[e.host_slug].push(e);
        });
        // Sort each artist's shows chronologically
        Object.keys(idx).forEach(k => idx[k].sort((x, y) => (x.start_datetime || "").localeCompare(y.start_datetime || "")));
        setArtists(Array.isArray(a) ? a : []);
        setEventsByArtist(idx);
        setTracks(Array.isArray(mu) ? mu : []);
      } catch (err) {
        console.error("FeaturedArtistsPage processing error:", err);
        // Render what we can — never leave the spinner stuck.
        setArtists(Array.isArray(a) ? a : []);
        setTracks(Array.isArray(mu) ? mu : []);
      } finally {
        setLoading(false);
      }
    }).catch((err) => {
      // Defensive: even if everything above fails, render the empty state
      // rather than spin forever (which was the user-reported "stall").
      if (cancelled) return;
      console.error("FeaturedArtistsPage fetch failed:", err);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, []);

  // Map featured-artists API shape into a uniform "card-friendly" object.
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  // Admin: move an artist up/down on the page; order is saved to the DB (featured_artists.order).
  const moveArtist = async (slug, dir) => {
    const list = [...artists];
    const i = list.findIndex((a) => a.slug === slug);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= list.length) return;
    [list[i], list[j]] = [list[j], list[i]];
    setArtists(list);
    try {
      await axios.put(`${API}/admin/featured-artists/reorder`, { order: list.map((a) => a.artist_id) }, { withCredentials: true });
      toast.success("Order saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't save order"); }
  };
  const cards = useMemo(() => {
    return (artists || []).filter(a => a && a.slug).map(a => ({
      slug: a.slug,
      name: a.name || a.slug,
      city: a.city || a.location || a.hometown || "",
      bio: a.bio || a.description || "",
      photo_url: a.photo_url || a.artist_photo_url || "",
      profile_url: a.profile_url || "",
      spotlight: !!a.spotlight,
      spotlight_eyebrow: a.spotlight_eyebrow || "",
      spotlight_note: a.spotlight_note || "",
      single_title: a.single_title || "",
      single_cover: a.single_cover || "",
      single_stream_url: a.single_stream_url || "",
      socials: {
        website: a.website,
        instagram: a.instagram_url || a.instagram,
        facebook: a.facebook_url || a.facebook,
        youtube: a.youtube_url || a.youtube,
        spotify: a.spotify_url || a.spotify,
        soundcloud: a.soundcloud_url || a.soundcloud,
        bandcamp: a.bandcamp_url || a.bandcamp,
      },
    }));
  }, [artists]);

  const spotlightCards = useMemo(() => cards.filter(c => c.spotlight), [cards]);
  const gridCards = useMemo(() => cards.filter(c => !c.spotlight), [cards]);

  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid="featured-artists-page">
      <Navbar />

      {/* Hero */}
      <div className="bg-gradient-to-br from-[#1A1A2E] via-[#5e3fa0] to-[#FFB347] text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
          <Link to="/events/music" className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm mb-4 transition-colors" data-testid="back-to-music">
            <ArrowLeft className="h-4 w-4" /> Back to the Calendar of the Sound
          </Link>
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="h-5 w-5 text-[#FFB347]" />
            <span className="text-xs font-bold tracking-widest uppercase text-[#FFB347]">Featured Artists</span>
          </div>
          <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl md:text-6xl font-medium tracking-tight mb-3">
            Featured <span className="text-[#FFB347]">Artists</span>
          </h1>
          <p className="text-base sm:text-lg text-white/85 max-w-2xl leading-relaxed">
            These are the bands, songwriters, and storytellers who are with us &mdash;
            and we&rsquo;re with them. Tap any card to meet the artist, hear the music,
            and find their next show.
          </p>

          {/* Hero album strip removed per user request — all artists now live
              in the grid below so the page is one cohesive roster instead of
              "two in the header, two below." The /contribute CTA still ships
              right above the artist grid for bands who want to join. */}
        </div>
      </div>

      <NorthStarSection />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        {/* Music cross-nav — Featured Artists / Whole Community Calendar /
            For Bands. Top-of-page anchor so visitors can hop between music
            surfaces with one tap. */}
        <div className="mb-6" data-testid="featured-artists-cross-nav-wrap">
          <MusicCrossNav />
        </div>


        {/* Listen-while-you-scroll banner — every artist card has an inline
            AlbumPlayer; this line tells visitors the play buttons keep the
            audio going as they browse the page. */}
        {!loading && tracks.length > 0 && (
          <div
            className="mb-6 rounded-xl bg-gradient-to-r from-[#FFB347]/15 via-[#9B59B6]/12 to-[#2E86AB]/15 border border-[#9B59B6]/25 px-4 py-3 flex items-center gap-3"
            data-testid="listen-while-you-scroll-banner-featured"
          >
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#9B59B6] to-[#FFB347] flex items-center justify-center flex-shrink-0">
              <Music className="h-4 w-4 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-['Playfair_Display'] text-sm sm:text-base font-semibold text-[#1A1A1A] m-0">
                Listen while you scroll <span className="text-[#9B59B6]">🎧</span>
              </p>
              <p className="text-xs text-[#5C5C5C] m-0">
                Hit <strong>Listen</strong> on any artist card — their <Hoard /> tracks keep playing as you browse the rest of the roster.
              </p>
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-center py-20 text-[#5C5C5C] animate-pulse">Loading the roster…</div>
        ) : cards.length === 0 ? (
          <div className="bg-white rounded-2xl border border-[#E2E0DC] p-10 text-center">
            <Music className="h-12 w-12 mx-auto text-[#E2E0DC] mb-3" />
            <p className="text-[#5C5C5C]">No featured artists yet — check back soon.</p>
          </div>
        ) : (
          <>
            {spotlightCards.map(a => (
              <SpotlightCard key={a.slug} artist={a} />
            ))}
            <div
              className="grid gap-5 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
              data-testid="featured-artists-grid"
            >
              {gridCards.map((a, idx) => (
                <div key={a.slug} className="relative" data-testid={`featured-artist-slot-${a.slug}`}>
                  {isAdmin && (
                    <div className="absolute -top-3 left-3 z-20 inline-flex items-center gap-1 rounded-full bg-[#1A1A1A] text-white px-2 py-1 shadow-lg" data-testid={`featured-artist-reorder-${a.slug}`}>
                      <GripVertical className="h-3.5 w-3.5 opacity-60" />
                      <span className="text-[10px] font-bold uppercase tracking-wider mr-1">#{idx + 1}</span>
                      <button type="button" onClick={() => moveArtist(a.slug, -1)} disabled={idx === 0} className="p-1 rounded-full hover:bg-white/15 disabled:opacity-25" aria-label="Move up" data-testid={`featured-artist-up-${a.slug}`}><ArrowUp className="h-4 w-4" /></button>
                      <button type="button" onClick={() => moveArtist(a.slug, 1)} disabled={idx === gridCards.length - 1} className="p-1 rounded-full hover:bg-white/15 disabled:opacity-25" aria-label="Move down" data-testid={`featured-artist-down-${a.slug}`}><ArrowDown className="h-4 w-4" /></button>
                    </div>
                  )}
                  <ArtistCard artist={a} upcoming={eventsByArtist[a.slug] || []} tracks={tracks} />
                </div>
              ))}
            </div>
          </>
        )}

        {/* Rolling-roster note — sits directly under the artist grid so visitors
            know the lineup is being actively curated, not a static list. */}
        <p
          className="mt-6 text-center text-xs sm:text-sm italic text-[#5C5C5C]"
          data-testid="featured-artists-rolling-note"
        >
          Music is being added from our grass roots community through <strong className="not-italic text-[#2E5C55]">August, 2026</strong>.
        </p>

        {/* Tip card — invite more artists */}
        <div className="mt-10 rounded-2xl bg-gradient-to-br from-[#FFB347]/10 to-[#7E5BBE]/10 border border-[#FFB347]/40 p-6 sm:p-8">
          <div className="flex items-start gap-3">
            <Sparkles className="h-5 w-5 text-[#FFB347] mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="text-sm font-semibold text-[#1A1A1A] mb-1">Are you an artist who wants to join?</h3>
              <p className="text-sm text-[#5C5C5C] leading-relaxed">
                Share a song or two for our music room, and we&rsquo;ll feature you here.
                Hit us with your next gig flyer and we&rsquo;ll pin it on{" "}
                <Link to="/events/music" className="text-[#2E5C55] underline">The Calendar of the Sound</Link>
                {" "}+{" "}
                <Link to="/events/music" className="text-[#2E5C55] underline">The Flyer Wall</Link>.
              </p>
            </div>
          </div>
        </div>
      </div>


      <Footer />
    </div>
  );
};

export default FeaturedArtistsPage;
